<template>
	<div class="withdraw-amount-form">
		<div v-if="!loading" class="withdraw-amount-form__balance">
			<span class="withdraw-amount-form__balance-label">Available Balance</span>
			<span class="withdraw-amount-form__balance-value">{{ formatAmountToDollar(availableCash, 2, currency) }}</span>
		</div>
		<template v-if="swapCurrency">
			<form v-if="!loading" class="withdraw-amount-form__form" @submit.prevent="storeData">
				<div class="withdraw-amount-form__input-group">
					<label class="withdraw-amount-form__label">Amount to withdraw ({{ getCurrencySymbol(currency) }})</label>
					<div class="withdraw-amount-form__input-wrapper">
						<span class="withdraw-amount-form__currency-symbol">{{ getCurrencySymbol(currency) }}</span>
						<NumberInput
							placeHolder="0.00"
							label="amount"
							:amount="payload.amount"
							:minNumber="minimumWithdrawal"
							:maxNumber="availableCash"
							type="text"
							name="amount"
							id="amount"
							@set="setAmount"
							@validate="validate"
						/>
					</div>
					<p class="withdraw-amount-form__equivalent" v-if="currencyRate">
						≈ {{ formatAmountToDollar(swapConversion, 2, swapCurrency) }}
					</p>
				</div>
				<label for="fullBalance" class="withdraw-amount-form__checkbox">
					<input type="checkbox" name="fullBalance" id="fullBalance" v-model="fullAmount" />
					<span>Withdraw full balance</span>
				</label>
				<button type="submit" class="withdraw-amount-form__btn">
					Move to {{ getCurrencySymbol(swapCurrency) }} Wallet
				</button>
			</form>
		</template>
		<template v-else>
			<form v-if="!loading" class="withdraw-amount-form__form" @submit.prevent="storeData">
				<div class="withdraw-amount-form__input-group">
					<label class="withdraw-amount-form__label">Amount to withdraw</label>
					<div class="withdraw-amount-form__input-wrapper">
						<span class="withdraw-amount-form__currency-symbol">{{ getCurrencySymbol(currency) }}</span>
						<NumberInput
							placeHolder="0.00"
							label="amount"
							:amount="payload.amount"
							:minNumber="minimumWithdrawal"
							:maxNumber="availableCash"
							type="text"
							name="amount"
							id="amount"
							@set="setAmount"
							@validate="validate"
						/>
					</div>
				</div>
				<label for="fullBalance" class="withdraw-amount-form__checkbox">
					<input type="checkbox" name="fullBalance" id="fullBalance" v-model="fullAmount" />
					<span>Withdraw full balance</span>
				</label>
				<button type="submit" class="withdraw-amount-form__btn">Continue</button>
			</form>
		</template>
		<SmallLoader v-if="loading" />
	</div>
</template>

<script>
import NumberInput from "@/components/wallet/NumberInput.vue";
import { formatAmountToDollar, getCurrencySymbol } from "@/utils/helpers.js";
import { getItem, setItem } from "@/utils/storage.js";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
	name: "WithdrawAmountForm",

	emits: ["storeData", 'close'],

	components: {
		NumberInput,
	},

	props: {
		availableCash: { type: Number, default: () => 0, required: true },
		minimumWithdrawal: { type: Number, default: () => 0, required: true },
		rate: { type: Number, default: () => 0, required: true },
		withdrawMethod: { type: String, default: () => "" },
	},

	inject: ["swapCurrency"],

	data() {
		return {
			validInput: {
				dollarAmount: false,
				nairaAmount: false,
			},
			payload: {
				amount: "",
			},
			withdrawPayload: {},
			dollarStatus: true,
			nairaStatus: true,
			fullAmount: false,
		};
	},

	computed: {
		...mapState({
			loading: (state) => state.smallLoading,
			currency: (state) => state.organisationModule.currency,
		}),
		...mapGetters("organisationModule", ["currencyRate"]),
		swapConversion() {
			if (this.swapCurrency) {
				return String(this.payload.amount * Number(this.currencyRate));
			}
			return "0";
		},
	},
	watch: {
		fullAmount(val) {
			if (val) {
				this.payload.amount = String(this.availableCash);
			}
		},
	},

	mounted() {
		this.getMinimumWithdrawal();
		this.withdrawPayload = getItem("withdrawalPayload");

		if (this.withdrawMethod.includes("Wallet")) {
			this.getRate();
		}
	},

	methods: {
		...mapActions("organisationModule", [
			"getMinimumWithdrawal",
			"convertCurrency",
			"convertWallet",
			"getWalletBalance",
		]),
    ...mapActions(['setSmallLoading', 'showToast']),

		formatAmountToDollar,
		getCurrencySymbol,
		async getRate() {
			const exchangeQuery = {
				from: this.currency,
				to: this.swapCurrency,
				amount: 1,
			};
			await this.convertCurrency(exchangeQuery);
		},

		setAmount(amount) {
			this.payload.amount = amount;
		},

		validate() {},

		storeData() {
			this.withdrawPayload.tokens = this.payload.amount;
			this.withdrawPayload.amount = this.payload.amount;

			setItem("withdrawalPayload", this.withdrawPayload);
			if (this.withdrawMethod.includes("Wallet")) {
				this.fundFromWallet();
			} else {
				this.$emit("storeData", "amountForm");
			}
		},

		async fundFromWallet() {
			const payload = {
				amount: this.payload.amount,
				from: this.currency,
				to: this.swapCurrency,
			};
			this.setSmallLoading(true);
			try {
				const response = await this.convertWallet(payload);
				const { message } = response.data;
				this.showToast({
					description: message,
					display: true,
					type: "success",
				});
				await this.getWalletBalance();
				this.setSmallLoading(false);
				this.$emit("close", "Fund Wallet");
			} catch (error) {
				this.setSmallLoading(false);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #6366F1;
$accent-light: #EEF2FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.withdraw-amount-form {
  &__balance {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: $bg-subtle;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 24px;
  }

  &__balance-label {
    font-size: 13px;
    color: $text-muted;
  }

  &__balance-value {
    font-size: 16px;
    font-weight: 600;
    color: $text-primary;
  }

  &__form {
    width: 100%;
  }

  &__input-group {
    margin-bottom: 16px;
  }

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $text-muted;
    margin-bottom: 12px;
  }

  &__input-wrapper {
    position: relative;

    :deep(.number-input__field) {
      padding-left: 48px;
      height: 56px;
      font-size: 24px;
      font-weight: 500;
      border-radius: 10px;

      &::placeholder {
        color: $border-color;
      }
    }
  }

  &__currency-symbol {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
    font-weight: 500;
    color: $text-primary;
    pointer-events: none;
    z-index: 1;
  }

  &__equivalent {
    margin: 8px 0 0;
    font-size: 13px;
    color: $text-muted;
  }

  &__checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    margin-bottom: 24px;

    input {
      width: 18px;
      height: 18px;
      accent-color: $accent-primary;
      cursor: pointer;
    }

    span {
      font-size: 14px;
      color: $text-secondary;
    }
  }

  &__btn {
    width: 100%;
    height: 48px;
    background: $btn-primary;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      background: $border-color;
      color: $text-muted;
      cursor: not-allowed;
    }
  }
}
</style>
